import { useState } from 'react';
import { useUpdateEffect } from 'react-use';

import {
  DEFAULT_STO_COMPLETION_TIME,
  MIN_STO_COMPLETION_TIME,
} from 'modules/campaigns/lib/constants';
import {
  FormBodyText,
  IndentedRowWrapper,
  SmallInputWrapper,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { ExpandableCheckbox } from 'modules/workflowNodeEditors/editorComponents';
import { Campaign } from 'modules/workflows';
import { InputText } from 'ui/inputs';
import { AiColor16 } from 'ui/icons';
import noop from 'utils/noop';

import { ErrorText } from '../BaseSendEditor.styles';
import { CurrentProject } from '../BaseSendEditor.types';
import { createSTOCampaignProps, isSTOEnabled, validateSTORange } from '../BaseSendEditor.utils';
import { StyledWarningBanner } from './DeliverySection.styles';

export const STO_AND_QH_OVERLAPPING_BANNER_TEST_ID = 'sto-and-qh-overlapping-banner-test-id';

type STOProps = Pick<Campaign, 'dataScienceOptimizations' | 'schedulerMsgParams'>;

export interface STOCheckboxProps {
  timeZone: CurrentProject['timeZone'];
  campaign: Partial<Campaign>;
  isDisabled?: boolean;
  warningText?: string;
  onChange?: (args: STOProps) => void;
}

export const STOCheckbox = ({
  isDisabled = false,
  onChange = noop,
  timeZone,
  campaign = {},
  warningText,
}: STOCheckboxProps) => {
  const currentHrs = campaign.schedulerMsgParams?.windowHours;
  const [isChecked, setIsChecked] = useState(isSTOEnabled(campaign.dataScienceOptimizations));
  const [windowHoursCache, setWindowHoursCache] = useState(
    currentHrs ?? DEFAULT_STO_COMPLETION_TIME,
  );
  const [rangeError, setRangeError] = useState(validateSTORange(campaign));

  const handleWindowHoursUpdate = (hrString: string) => {
    if (!hrString) {
      onChange(createSTOCampaignProps(timeZone, hrString));
    } else {
      try {
        const hrNumber = Number(hrString);
        setWindowHoursCache(hrNumber);
        onChange(createSTOCampaignProps(timeZone, hrNumber));
      } catch {
        // do nothing
      }
    }
  };

  useUpdateEffect(() => {
    const sto = isChecked
      ? createSTOCampaignProps(timeZone, windowHoursCache)
      : { dataScienceOptimizations: [], schedulerMsgParams: null };
    onChange(sto);
  }, [isChecked]);

  return (
    <ExpandableCheckbox
      caption="Optimize the delivery of this message based on past campaign performance & user behavior."
      id="sto"
      isChecked={isChecked}
      isDisabled={isDisabled}
      onEnabledUpdate={setIsChecked}
      label="Send time optimization"
      labelSupplement={<AiColor16 />}
    >
      <IndentedRowWrapper>
        <FormBodyText>Complete within</FormBodyText>
        <SmallInputWrapper>
          <InputText
            hasError={rangeError.hasError}
            id="sto-completion-time-input"
            maxValue={DEFAULT_STO_COMPLETION_TIME}
            minValue={MIN_STO_COMPLETION_TIME}
            onBlur={() => {
              if (!currentHrs) {
                handleWindowHoursUpdate(`${DEFAULT_STO_COMPLETION_TIME}`);
              } else {
                const err = validateSTORange(createSTOCampaignProps(timeZone, currentHrs));
                setRangeError(err);
              }
            }}
            onChange={handleWindowHoursUpdate}
            placeholder={`${DEFAULT_STO_COMPLETION_TIME}`}
            Suffix={<span>{currentHrs === 1 ? 'hour' : 'hours'}</span>}
            type="number"
            value={`${currentHrs}`}
            isDisabled={isDisabled}
          />
        </SmallInputWrapper>
      </IndentedRowWrapper>
      {rangeError.hasError && <ErrorText>{rangeError.message}</ErrorText>}
      {warningText && (
        <StyledWarningBanner data-test={STO_AND_QH_OVERLAPPING_BANNER_TEST_ID}>
          {warningText}
        </StyledWarningBanner>
      )}
    </ExpandableCheckbox>
  );
};

export default STOCheckbox;
